import React, { useContext } from "react";
import { styled } from "@mui/system";
import { RouteComponentProps, withRouter } from "react-router";
import { observer } from "mobx-react";

import { ApplicationListing } from "api";

import { useStores } from "contexts/storeContext";

import themes from "components/themes";

import { SingleSideMenuItem } from "./constants/sideMenuItem";
import { menuTypes } from "./constants/menuTypes";
import {
  menus as directoryMenus,
  PlatformContext,
  PlatformStore,
} from "@upptic/module-directory";

import SideMenuNavigation from "../../v2/components/SideMenu/SideMenu";

import { truncate } from "utils/strings";

import SideMenuItemList from "./components/SideMenuItemList";
import { createUrlWithParams } from "v2/utils/url";
import { UserPermissions } from "v2/api/types";

interface SideMenuComponentProps extends RouteComponentProps<{}> {
  collapsed: boolean;
  application: ApplicationListing | null;
}

function SideMenuComponent({
  collapsed,
  application,
  location,
  history,
}: SideMenuComponentProps) {
  const { layoutStore } = useStores();
  const platformStore = useContext<PlatformStore>(PlatformContext);

  // TODO: to use in feature
  // const filterMenuItemsByPermissions = (sideMenuElements: SingleSideMenuItem[], permissions: UserPermissions[]): SingleSideMenuItem[] => {
  //   const elements: (SingleSideMenuItem | undefined)[] = sideMenuElements.map(singleMenuElement => {
  //     const { items, route } = singleMenuElement
  //     const routeObject = allRoutes.find(singleRoute => singleRoute.isRouteUrl(route || ''))

  //     if (routeObject && !routeObject.hasUserAccess(permissions)) {
  //       return undefined
  //     }

  //     const filteredItems: SingleSideMenuItem[] = items && items.length ? filterMenuItemsByPermissions(items, permissions) : []

  //     return {
  //       ...singleMenuElement,
  //       items: filteredItems && filteredItems.length ? filteredItems : undefined
  //     }
  //   })

  //   return elements.filter((elem): elem is SingleSideMenuItem => !!elem)
  // }
  const routeParams = platformStore.currentRoute?.params as any;
  const menuType = directoryMenus[layoutStore.sideMenuMode]?.type;
  const currentApplicationCode = platformStore.selectedApplication?.code;

  const menuItems = React.useMemo(() => {
    const directoryResult = directoryMenus[layoutStore.sideMenuMode]?.entries;
    if (Array.isArray(directoryResult)) {
      return directoryResult
        .map((entry) => {
          // replace placeholder
          const entryObj = { ...entry };
          if (entryObj.route?.includes(":")) {
            entryObj.route = createUrlWithParams(entryObj.route, {
              ...routeParams,
              applicationCode: currentApplicationCode,
            });
          }
          return entryObj;
        })
        .filter(({ permissions }) => {
          if (!permissions) return true;
          if (!permissions.length) return false;
          if (
            permissions.every((code) =>
              platformStore.currentUserFrontendPermissions.includes(
                code as UserPermissions,
              ),
            )
          )
            return true;
          return false;
        });
    }
    if (typeof directoryResult === "function")
      return directoryResult(
        collapsed,
        application,
        platformStore.currentUserFrontendPermissions,
      );

    const menuElementsGetter = menuTypes[layoutStore.sideMenuMode];
    return menuElementsGetter(
      collapsed,
      application,
      platformStore.currentUserFrontendPermissions as UserPermissions[],
      platformStore?.currentClient?.code,
    );
  }, [
    collapsed,
    layoutStore.sideMenuMode,
    application,
    platformStore.currentUserFrontendPermissions,
    platformStore.currentClient,
    currentApplicationCode,
    routeParams,
  ]);

  if (menuType === "mui") {
    return <SideMenuNavigation menuItems={menuItems} collapsed={collapsed} />;
  }

  return (
    <MainSidebar collapsed={collapsed}>
      <Items>
        {menuItems.map((item) => {
          return (
            <React.Fragment key={`menu-container-${item.title}`}>
              {item.route ? (
                <SideMenuItemList
                  key={`menu-1-${item.title}`}
                  item={item}
                  items={item.items}
                  currentRoute={location.pathname}
                  depth={1}
                  hideTitle={collapsed}
                  onItemClick={(i: SingleSideMenuItem) =>
                    i.route && history.push(i.route)
                  }
                  highlighted={item.highlighted}
                />
              ) : (
                <>
                  <NonClickableItem>
                    {truncate(item.title, 17)}
                  </NonClickableItem>
                  {!collapsed &&
                    item.items &&
                    item.items.map((nested) => {
                      return (
                        <NestedSideMenuItemList
                          key={`menu-2-${nested.title}`}
                          item={nested}
                          items={nested.items}
                          currentRoute={location.pathname}
                          depth={1}
                          hideTitle={collapsed}
                          onItemClick={(i: SingleSideMenuItem) =>
                            i.route && history.push(i.route)
                          }
                        />
                      );
                    })}
                </>
              )}
            </React.Fragment>
          );
        })}
      </Items>
    </MainSidebar>
  );
}

const MainSidebar = styled("aside", {
  shouldForwardProp: (prop: string) => !["collapsed"].includes(prop),
})<{ collapsed: boolean }>`
  background-color: ${themes.colors.background};
  min-height: 100%;
  width: ${(props: any) => (props.collapsed ? "80px" : "250px")};
  transition: width 0.4s;

  a:hover {
    text-decoration: none;
  }
`;

const Items = styled("div")`
  position: fixed;
  top: 0;
  width: inherit;
  z-index: ${(props: any) => props.theme.zIndexes.sideMenu};
  margin-top: 60px;
`;

const NestedSideMenuItemList = styled(SideMenuItemList)`
  padding-left: 20px;
`;

const NonClickableItem = styled("div")`
  color: ${themes.colors.white};
  opacity: 0.4;
  font-size: 16px;
  font-weight: 300;
  height: 60px;
  padding: 20px 15px;
  border-top: 2px solid ${themes.colors.backgroundDark};
  overflow: hidden;
  white-space: nowrap;
`;

export const SideMenu = withRouter(observer(SideMenuComponent));
